<template>
  <div
    :style="styles"
    :class="['rte', hasError ? 'rte--invalid' : '']"
  >
    <div class="rte--holder">
      <div
        v-if="editor"
        class="rte--controls"
      >
        <div class="rte--group">
          <RteButton
            :class="{ 'is-active': editor.isActive('bold') }"
            @click.prevent="editor.chain().focus().toggleBold().run()"
          >
            <i class="far fa-bold" />
          </RteButton>
          <RteButton
            :class="{ 'is-active': editor.isActive('italic') }"
            @click.prevent="editor.chain().focus().toggleItalic().run()"
          >
            <i class="far fa-italic" />
          </RteButton>
          <RteButton
            :class="{ 'is-active': editor.isActive('underline') }"
            @click.prevent="editor.chain().focus().toggleUnderline().run()"
          >
            <i class="far fa-underline" />
          </RteButton>
          <RteButton
            :class="{ 'is-active': editor.isActive('strike') }"
            @click.prevent="editor.chain().focus().toggleStrike().run()"
          >
            <i class="far fa-strikethrough" />
          </RteButton>
          <RteButton
            v-if="code"
            :class="{ 'is-active': editor.isActive('code') }"
            @click.prevent="editor.chain().focus().toggleCode().run()"
          >
            <i class="far fa-code" />
          </RteButton>
          <RteButton
            v-if="bulletList"
            :class="{ 'is-active': editor.isActive('bulletList') }"
            @click.prevent="editor.chain().focus().toggleBulletList().run()"
          >
            <i class="fal fa-list-ul" />
          </RteButton>
          <RteButton
            v-if="orderedList"
            :class="{ 'is-active': editor.isActive('orderedList') }"
            @click.prevent="editor.chain().focus().toggleOrderedList().run()"
          >
            <i class="fal fa-list-ol" />
          </RteButton>
          <RteButton
            v-if="codeBlock"
            :class="{ 'is-active': editor.isActive('codeBlock') }"
            @click.prevent="editor.chain().focus().toggleCodeBlock().run()"
          >
            <i class="fal fa-brackets-curly" />
          </RteButton>
          <RteButton
            v-if="blockquote"
            :class="{ 'is-active': editor.isActive('blockquote') }"
            @click.prevent="editor.chain().focus().toggleBlockquote().run()"
          >
            <i class="fal fa-quote-right" />
          </RteButton>
          <div
            v-show="hasImages"
            ref="imageDropdown"
            class="rte--group"
          >
            <RteButton
              @click.prevent="openImageSelection()"
            >
              <i class="fas fa-image" />
            </RteButton>
            <div
              v-show="showImageSelection"
              class="rte--group-dropdown"
            >
              <BaseItemList
                v-if="hasImages"
                :list-item-height="48"
                :list-active="showImageSelection"
                :items="imageList"
                @select="addImage($event)"
              />
            </div>
          </div>
          <RteButton
            @click.prevent="editor.chain().focus().setHorizontalRule().run()"
          >
            <i class="fas fa-horizontal-rule" />
          </RteButton>
        </div>
        <div class="rte--group">
          <RteButton
            @click.prevent="editor.chain().focus().undo().run()"
          >
            <i class="far fa-undo" />
          </RteButton>
          <RteButton
            @click.prevent="editor.chain().focus().redo().run()"
          >
            <i class="far fa-redo" />
          </RteButton>
        </div>
      </div>
      <div
        style="position: relative;"
        @click.prevent="editor.chain().focus()"
      >
        <div
          v-if="!value"
          class="rte--label"
        >
          <span>{{ label }}</span>
        </div>
        <editor-content
          :editor="editor"
          class="rte--content"
        />
      </div>
    </div>
    <div
      v-if="hasError"
      class="rte-hint"
    >
      <div
        v-if="hasError"
        class="rte--error"
        v-html="errorMessage"
      />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { isString } from 'lodash';
import Image from './image';

import RTEImageList from '../../../utils/RTEImageList';
import ClickOutside from '../../../shared/mixins/ClickOutside';

import BaseItemList from '../base/BaseItemList.vue';
import RteButton from './RteButton.vue';

export default {
  name: 'RichTextEditor',

  components: {
    EditorContent,
    RteButton,
    BaseItemList,
  },

  mixins: [
    ClickOutside,
  ],

  model: {
    prop: 'value',
    event: 'update',
  },

  props: {
    name: String,
    images: {
      type: RTEImageList,
      default: null,
    },
    initialContent: {
      type: String,
      default: null,
    },
    error: {
      type: [String, Array],
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    content: {
      type: String,
    },
    value: {
      type: String,
      default: '',
    },
    filled: {
      type: String,
      default: null,
    },
    code: {
      type: Boolean,
      default: false,
    },
    bulletList: {
      type: Boolean,
      default: false,
    },
    orderedList: {
      type: Boolean,
      default: false,
    },
    codeBlock: {
      type: Boolean,
      default: false,
    },
    blockquote: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      editor: null,
      showImageSelection: false,
      imageList: [
        {
          name: '',
          url: '',
        },
      ],
    };
  },

  computed: {
    errorMessage() {
      return Array.isArray(this.error) && this.error[0] ? this.error[0] : this.error;
    },
    hasError() {
      return !!this.errorMessage;
    },
    hasImages() {
      return this.images && this.images.length > 0;
    },
    styles() {
      let filled = 'var(--rte-filled-color-base)';

      if (isString(this.filled)) {
        if (this.filled.includes('#')) {
          filled = this.filled;
        } else {
          filled = `var(--${this.filled})`;
        }
      }

      return {
        '--rte-filled-color': filled,
      };
    },
  },

  watch: {
    value(value) {
      // HTML
      const isSame = this.editor.getHTML() === value;

      // JSON
      // const isSame = JSON.stringify(this.editor.getJSON()) === JSON.stringify(value)

      if (isSame) {
        return;
      }

      this.editor.commands.setContent(value, false);
    },
    showImageSelection(val) {
      if (val) {
        this.outsideClick('add');
      } else {
        this.outsideClick('remove');
      }
    },
    images() {
      this.imageList = this.images?.getItemList();
    },
  },

  mounted() {
    this.editor = new Editor({
      extensions: [StarterKit, Underline, Image],
      content: this.value,
      onUpdate: () => {
        // HTML
        this.$emit('update', this.editor.getHTML());

        // JSON
        // this.$emit('input', this.editor.getJSON())
      },
    });

    this.imageList = this.images?.getItemList();
  },

  beforeDestroy() {
    this.editor.destroy();
  },

  methods: {
    outsideClick(type) {
      const imageGroup = this.$refs.imageDropdown;
      this.clickOutside(type, imageGroup, () => { this.showImageSelection = false; });
    },
    addImage(id) {
      this.editor.chain().focus().addImage(id).run();
      this.showImageSelection = false;
    },
    openImageSelection() {
      this.imageList = this.images?.getItemList();

      this.showImageSelection = !this.showImageSelection;
    },
  },
};
</script>

<style lang="scss">
/* Basic editor styles */
.ProseMirror {
    > * + * {
        margin-top: 0.75em;
    }

    &:focus-visible {
        outline: none;
    }

    ul,
    ol {
        padding: 0 1rem;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        line-height: 1.1;
    }

    code {
        background-color: rgba(#616161, 0.1);
        color: #616161;
    }

    pre {
        background: #0d0d0d;
        color: #fff;
        font-family: "JetBrainsMono", monospace;
        padding: 0.75rem 1rem;
        border-radius: 0.5rem;

        code {
            color: inherit;
            padding: 0;
            background: none;
            font-size: 0.8rem;
        }
    }

    img {
        max-width: 100%;
        height: auto;
    }

    blockquote {
        padding-left: 1rem;
        border-left: 2px solid rgba(#0d0d0d, 0.1);
    }

    hr {
        border: none;
        border-top: 2px solid rgba(#0d0d0d, 0.1);
        margin: 2rem 0;
    }
}

.rte {
  position: relative;

  &--holder {
    border-radius: 25px;
  }

  &--label {
    position: absolute;
    top: 1em;
    padding-left: 1.5em;
  }

  &:focus-within .rte--label {
    display: none;
  }

  &--controls {
    display: flex;
    justify-content: space-between;
    padding: 0 1rem;
    border: 1px solid #f2f2f2;
    border-top-left-radius: 25px;
    border-top-right-radius: 25px;
    background-color: #f2f2f2;
    border-bottom: none;
  }

  &--group {
    display: flex;
    position: relative;
  }

  &--group-dropdown {
    position: absolute;
    top: 2.5rem;
    width: 20rem;
    width: max-content;
    display: flex;
    flex-direction: column;
    z-index: 1;
    background-color: #fff;
    border: 1px solid #dee2e6;
    border-radius: 5px;
    overflow: auto;
  }

  &--content {
    background-color: var(--rte-filled-color);
    border: 1px solid #dee2e6;
    border-top: none;
    border-bottom-left-radius: 25px;
    border-bottom-right-radius: 25px;
    height: 20rem;
    overflow: auto;
    padding: 1rem 1.5rem;
  }

  &--invalid {
    .rte--holder {
      box-shadow: 0 0 5px 0 var(--forms-errors);
    }

    .rte-hint {
      display: flex;
      justify-content: flex-end;
      grid-column-gap: 1em;
      padding: 0.25em 0;

      .rte--error {
        flex: 1;
        display: block;
        font-size: 12px;
        color: var(--forms-errors);
        line-height: 1.25;
      }
    }
  }
}
</style>
